import socketIOClient from "socket.io-client";
import api from "../api/axios";

import { updateScoresForCompany, updateJobScore, updateJobScoresBatch } from '../redux/JobSlice/jobSlice';
import { updateProgress, addSignal, scanComplete, scanFailed, fetchPerplexitySignals } from "../redux/HSignalSlice/hsignalSlice";
import { setCompanyError, setCompanyLoading, setSearchComplete, updateContactsForCompany } from "../redux/ContactSlice/contactSlice";

let socket;

export const connectSocket = (userId, dispatch) => {
    if (socket) return;

    console.log('[Socket Manager] Initializing new connection...');
    // Get the base URL with fallbacks to ensure it's always defined
    const baseURL = api.defaults.baseURL || process.env.REACT_APP_API_BASE_URL || window.location.origin;
    console.log('[Socket Manager] Using base URL:', baseURL);
    socket = socketIOClient(baseURL, {
        extraHeaders: {
            "ngrok-skip-browser-warning": "true"
        },
        transports: ['polling', 'websocket'] // Ensure polling works even with ngrok warning
    });

    // Connecting socket to user
    socket.on('connect', () => {
        console.log('✅ [Socket Manager] Connected to server!');
        socket.emit('register-user', userId);
    });

    socket.on('score-updated', (data) => {
        console.log('➡️ [Socket Manager] Received score update:', data);
        console.log('   🔍 Company ID:', data.company?._id);
        console.log('   🔍 Company Name:', data.company?.name);
        console.log('   🔍 Has scores:', !!data.company?.scores);
        if (data.company?.scores) {
            console.log('   🔍 Score keys:', Object.keys(data.company.scores));
        }
        if (data.company) {
            console.log('   ➡️ Dispatching updateScoresForCompany to Redux...');
            dispatch(updateScoresForCompany(data.company));
            console.log('   ✅ Dispatched updateScoresForCompany');
        } else {
            console.warn('   ⚠️ No company data in score-updated event!');
        }
    });

    // Job-level score updates (rank/score for a single job)
    socket.on('job-score-updated', (data) => {
        console.log('➡️ [Socket Manager] Received job-score-updated:', data);
        if (!data || (!data.jobId && !data._id)) {
            console.warn('   ⚠️ job-score-updated missing jobId/_id');
            return;
        }
        try {
            // Dispatch the full payload so reducers can merge reasons, jdSummary, etc.
            dispatch(updateJobScore({
                jobId: data.jobId || data._id,
                _id: data._id || data.jobId,
                score: data.score,
                scoreBreakdown: data.scoreBreakdown,
                scoreUpdatedAt: data.scoreUpdatedAt,
                reasons: data.reasons,
                reasonCodes: data.reasonCodes,
                signalEvidence: data.signalEvidence,
                jdSummary: data.jdSummary,
                descriptionSections: data.descriptionSections,
                roleMatches: data.roleMatches,
                bestRoleMatch: data.bestRoleMatch,
                matchLabel: data.matchLabel,
                competitionLabel: data.competitionLabel,
                rankingConfidence: data.rankingConfidence,
                personalizationGeneratedAt: data.personalizationGeneratedAt,
                personalizationSource: data.personalizationSource
            }));
            console.log('   ✅ Dispatched updateJobScore (full payload)');
        } catch (err) {
            console.error('   ❌ Failed to dispatch updateJobScore:', err.message || err);
        }
    });

    // Batched job-level score updates (reduces dispatch/event overhead)
    socket.on('job-scores-batch', (payload) => {
        const updates = Array.isArray(payload?.updates) ? payload.updates : [];
        if (!updates.length) return;
        try {
            dispatch(updateJobScoresBatch(updates));
        } catch (err) {
            console.error('   ❌ Failed to dispatch updateJobScoresBatch:', err.message || err);
        }
    });

    // Fetching hiring signals
    socket.on('progress', (data) => {
        console.log('➡️ [Socket Manager] Received progress:', data.message);
        dispatch(updateProgress(data));
    });

    socket.on('signal-found', (signal) => {
        console.log('➡️ [Socket Manager] Received new signal:', signal.companyName);
        dispatch(addSignal(signal));
    });

    socket.on('scan-complete', (data) => {
        console.log('✅ [Socket Manager] Scan complete!');
        dispatch(scanComplete(data));
        dispatch(fetchPerplexitySignals({ limit: 100, skip: 0, force: true }));
    });

    socket.on('scan-error', (data) => {
        console.error('❌ [Socket Manager] Received scan error:', data.message);
        dispatch(scanFailed(data));
    });

    socket.on('connect_error', (err) => {
        console.error('❌ [Socket Manager] Connection Error:', err.message);
    });

    // Fetch scrapped contacts
    socket.on('processing-company', (data) => {
        console.log(`➡️ [Socket Manager] Received processing-company for org: ${data.organizationId}`);
        dispatch(setCompanyLoading(data));
    });

    socket.on('contacts-ready', (data) => {
        console.log(`➡️ [Socket Manager] Received contacts-ready for org: ${data.organizationId}`);
        dispatch(updateContactsForCompany(data));
    });

    socket.on('search-complete', (data) => {
        console.log('✅ [Socket Manager] Real-time contact search complete!');
        dispatch(setSearchComplete(data)); // You can reuse this or create a new action
    });

    // 4. An error occurred while fetching contacts for a specific company
    socket.on('search-error', (data) => {
        console.error(`❌ [Socket Manager] Received search-error for org: ${data.organizationId}`, data.message);
        dispatch(setCompanyError(data));
    });
};


// Disconnects from the WebSocket server
export const disconnectSocket = () => {
    if (socket) {
        console.log('🔌 [Socket Manager] Disconnecting socket.');
        socket.disconnect();
        socket = null;
    }
};
